import { createBrowserRouter } from 'react-router';
import App, { GuardedLayout } from '~/App';
import { NotFoundPage } from '~/pages/NotFoundPage';
import { sandboxRoutes } from './sandbox.routes';
import { bookingRoutes } from './booking.routes';
import { demoRoutes } from './demo.routes';
import { subscriptionRoutes } from './subscription.routes';
import { lessonRoutes } from './lessons.routes';
import { authRoutes } from './auth.routes';
import { unlockRoutes } from './unlock.routes';
import { WaiverGate } from '~/features/waivers';


/**
 * Main Application Router
 *
 * Routes are organized by domain:
 * - unlock: SMS door-unlock link (unauthenticated, unlinked)
 * - sandbox: Testing and demonstration routes
 * - booking: Bay bookings and management
 * - subscription: Memberships and passes
 *
 * Two tiers under the root layout. `unlockRoutes` sits directly under `App` because it has
 * no `club-id` / `tenant-handle` in the URL — its token supplies both — and
 * `MissingParamsGuard` would hard-error before the exchange could run. Everything else nests
 * under `GuardedLayout`, which is where that guard now lives.
 */
export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />, // Root layout: Providers, dev tools, error boundaries
    children: [
      // Ungated — resolves its own tenant + club from the link token.
      ...unlockRoutes,

      {
        element: <GuardedLayout />, // requires club-id + tenant-handle
        children: [
          // Sandbox routes (for testing patterns)
          ...sandboxRoutes,

          // Waiver gate — signed-in members must sign before booking or
          // subscribing. Guests pass through (no session to sign with) and are
          // asked at checkout instead. Deliberately does NOT wrap authRoutes:
          // account-setup / reset-password must stay reachable for a member who
          // is authenticated but has not finished setup — that page asks for the
          // waiver itself rather than being gated into it.
          //
          // Three capture surfaces now, one per way a member arrives: checkout
          // (guest), account setup (invited/imported), and this gate (everyone
          // else, and any `resign_scope: 'all'` republish).
          {
            element: <WaiverGate />,
            children: [
              // Production routes (to be implemented)
              ...bookingRoutes,

              // Member demo booking (S3) — deep-linked only, no nav entry.
              ...demoRoutes,

              ...subscriptionRoutes,

              ...lessonRoutes,
            ],
          },

          ...authRoutes,

          // Catch-all 404 route (must be last!)
          {
            path: '*',
            element: <NotFoundPage />,
          },
        ],
      },
    ],
  },
]);
